/**
 * Analyse d'un COPIER-COLLER de la page YouTube « Abonnements »
 * (youtube.com/feed/channels → Ctrl+A puis Ctrl+C).
 *
 * Le texte collé est très bruité : menus latéraux (« Accueil », « Shorts »,
 * « Abonnements », « Historique »…), en-têtes (« Toutes les chaînes »,
 * « Pertinence »), boutons (« S'abonner », « Abonné »), compteurs
 * (« 1,2 M d'abonnés », « 345 vidéos »), descriptions de chaînes, pied de page…
 * en FRANÇAIS comme en ANGLAIS selon la langue du compte.
 *
 * Heuristiques (fonction pure, sans réseau) :
 *   1. On découpe en lignes, on nettoie (espaces insécables, puces « • »).
 *   2. Une ligne « ANCRE » = une ligne qui commence par un @handle
 *      (« @Squeezie », « @Squeezie•19,5 M d'abonnés ») OU un compteur
 *      d'abonnés (« 19,5 M d'abonnés », « 2.3M subscribers »). Chaque carte de
 *      chaîne de la page contient au moins une ancre.
 *   3. MODE ANCRÉ (s'il y a au moins une ancre) : le NOM d'une chaîne est la
 *      dernière ligne « candidate » (voir 5) vue juste AVANT son ancre. Deux
 *      ancres consécutives (@handle puis compteur) appartiennent à la même
 *      chaîne → on complète l'entrée courante au lieu d'en créer une autre.
 *      Une ancre @handle sans nom devant → on garde le @handle seul (il sera
 *      résolu directement par l'API, forHandle). Les lignes du menu latéral
 *      (qui listent aussi quelques chaînes, sans ancre) sont ainsi ignorées.
 *   4. MODE LIBRE (aucune ancre, ex. liste recopiée à la main) : toutes les
 *      lignes candidates sont gardées.
 *   5. Une ligne est CANDIDATE si ce n'est pas du bruit : pas une entrée de
 *      menu/bouton connue (liste FR/EN ci-dessous), pas un compteur (abonnés,
 *      vidéos, vues), pas une date relative (« il y a 3 jours »), pas une URL,
 *      pas un nombre seul, ≤ 60 caractères et ≤ 8 mots (au-delà = description),
 *      pas une phrase qui finit par un point.
 *   6. Dédoublonnage insensible à la casse/aux accents (par @handle s'il est
 *      connu, sinon par nom).
 *
 * Exemple (FR) :
 *   Accueil / Shorts / Abonnements / Toutes les chaînes / Pertinence /
 *   Squeezie / @Squeezie•19,5 M d'abonnés / Vidéos de gaming… / Abonné /
 *   Cyprien / @cyprien / • / 14,2 M d'abonnés / Abonné
 *   → [{ name: 'Squeezie', handle: '@Squeezie' }, { name: 'Cyprien', handle: '@cyprien' }]
 */

export type PastedChannel = { name: string; handle?: string }

/** Entrées de menus / boutons / en-têtes YouTube à ignorer (FR + EN), normalisées. */
const NOISE = new Set([
  // En-tête / navigation
  'passer au contenu principal', 'skip navigation', 'rechercher', 'search', 'recherche vocale', 'search with your voice',
  'creer', 'create', 'notifications', 'fr', 'en', 'be', 'ch', 'ca', 'us', 'uk', 'premium', 'youtube', 'menu', 'guide',
  'accueil', 'home', 'shorts', 'abonnements', 'subscriptions', 'vous', 'you', 'bibliotheque', 'library',
  'historique', 'history', 'playlists', 'vos videos', 'your videos', 'mes videos', 'regarder plus tard', 'watch later',
  'videos j\'aime', 'liked videos', 'vos clips', 'your clips', 'telechargements', 'downloads', 'achats', 'purchases',
  'votre chaine', 'your channel', 'afficher plus', 'show more', 'afficher moins', 'show less', 'plus', 'more',
  'explorer', 'explore', 'tendances', 'trending', 'musique', 'music', 'films', 'movies', 'films et series', 'movies & tv',
  'en direct', 'live', 'jeux video', 'gaming', 'actualites', 'news', 'sport', 'sports', 'apprentissage', 'learning',
  'cours', 'courses', 'mode et beaute', 'fashion & beauty', 'podcasts', 'podcast', 'shopping', 'lieux', 'places',
  'plus de contenus youtube', 'plus de youtube', 'more from youtube', 'youtube premium', 'youtube music', 'youtube kids',
  'youtube studio', 'youtube tv', 'parcourir les chaines', 'browse channels', 'gerer', 'manage',
  'parametres', 'settings', 'historique des signalements', 'report history', 'aide', 'help',
  'envoyer des commentaires', 'send feedback',
  // En-tête de la page des chaînes
  'toutes les chaines', 'all channels', 'chaines', 'channels', 'pertinence', 'relevance', 'most relevant',
  'nouvelle activite', 'new activity', 'de a a z', 'a-z', 'a a z', 'trier par', 'sort by',
  // Boutons des cartes
  's\'abonner', 'subscribe', 'abonne', 'abonnee', 'abonne(e)', 'subscribed', 'se desabonner', 'unsubscribe',
  'toutes', 'all', 'personnalisees', 'personalized', 'aucune', 'none', 'verifie', 'verified',
  // Pied de page
  'a propos', 'about', 'presse', 'press', 'droits d\'auteur', 'copyright', 'nous contacter', 'contact us',
  'createurs', 'creators', 'annoncer', 'advertise', 'developpeurs', 'developers', 'conditions', 'terms',
  'confidentialite', 'privacy', 'regles et securite', 'policy & safety', 'premiers pas avec youtube',
  'how youtube works', 'tester de nouvelles fonctionnalites', 'test new features', 'nfl sunday ticket',
])

/** Normalise pour comparer : minuscules, sans accents, apostrophes/guillemets unifiés, espaces réduits. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '')
    .replace(/[’‘`´]/g, '\'').replace(/[«»“”"]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim()
}

// @handle en début de ligne (lettres Unicode, chiffres, _ . - · autorisés par YouTube).
const HANDLE_RE = /^@([\p{L}\p{N}_.\-·]{2,40})/u
// Compteur d'abonnés : « 19,5 M d'abonnés », « 1 234 abonnés », « 2.3M subscribers », « Aucun abonné ».
const SUBS_RE = /(\d[\d\s.,]*\s*(k|m|md|mrd|b|mille|million|millions|milliard|milliards)?\.?\s*(d'\s*)?(abonne|subscriber))|^(aucun abonne|no subscribers)/
// Autres compteurs / méta : vidéos, vues, dates relatives.
const META_RE = /(\d[\d\s.,]*\s*(k|m|md|b)?\.?\s*(videos?|vues?|views?|shorts?))|^il y a |\bago$|^\d+[\s.,:]*\d*$|^[•·|\-–—\s]+$|^©|google llc/

/** Est-ce une ancre ? Renvoie le @handle trouvé (ou '' pour un compteur seul), null sinon. */
function anchorOf(line: string): string | null {
  const h = HANDLE_RE.exec(line)
  if (h) return '@' + h[1].replace(/[.\-·]+$/, '')
  return SUBS_RE.test(norm(line)) ? '' : null
}

/** Ligne candidate au titre d'une chaîne ? */
function isCandidate(line: string): boolean {
  const n = norm(line)
  if (!n || n.length > 60) return false
  if (NOISE.has(n)) return false
  if (META_RE.test(n)) return false
  if (/^(https?:\/\/|www\.)/.test(n)) return false
  if (!/[\p{L}\p{N}]/u.test(n)) return false
  const words = n.split(' ').length
  if (words > 8) return false
  // Phrase de description (plusieurs mots et point final).
  if (words > 4 && /[.!?…]$/.test(n)) return false
  return true
}

/** Version détaillée : noms + @handles associés (quand on les a). */
export function parseSubscriptionsPasteDetailed(text: string): PastedChannel[] {
  // En JS, \s couvre déjà les espaces insécables (U+00A0, U+202F) des nombres FR.
  const lines = String(text || '')
    .split(/\r?\n|\r/)
    .map(l => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean)

  const hasAnchor = lines.some(l => anchorOf(l) !== null)
  const out: PastedChannel[] = []

  if (!hasAnchor) {
    for (const l of lines) if (isCandidate(l)) out.push({ name: l })
  } else {
    let pending: string | null = null      // dernier nom candidat vu depuis la dernière ancre
    let current: PastedChannel | null = null // entrée en cours (ancres consécutives)
    for (const l of lines) {
      const a = anchorOf(l)
      if (a !== null) {
        if (pending) {
          current = { name: pending, ...(a ? { handle: a } : {}) }
          out.push(current)
          pending = null
        } else if (current && (a ? !current.handle : true)) {
          // Même carte : @handle puis compteur (ou l'inverse).
          if (a) current.handle = a
        } else if (a) {
          // Carte sans nom lisible (ou 2e @handle d'affilée) → @handle seul.
          current = { name: a, handle: a }
          out.push(current)
        }
        continue
      }
      if (isCandidate(l)) { pending = l; current = null }
      // Bruit : on l'ignore sans casser le groupe en cours (« • » entre @handle et compteur).
    }
  }

  // Dédoublonnage (par handle si connu, sinon par nom).
  const seen = new Set<string>()
  const res: PastedChannel[] = []
  for (const c of out) {
    const keys = [c.handle ? 'h:' + norm(c.handle) : '', 'n:' + norm(c.name)].filter(Boolean)
    if (keys.some(k => seen.has(k))) continue
    keys.forEach(k => seen.add(k))
    res.push(c)
  }
  return res
}

/** Noms de chaînes extraits du collage (ou @handle quand le nom manque), dédoublonnés. */
export function parseSubscriptionsPaste(text: string): string[] {
  return parseSubscriptionsPasteDetailed(text).map(c => c.name)
}
